/**
 * 证件/资质文件预览-上传组件（FE-E3b 注入式下沉）：渲染骨架与四端原实现逐字一致
 * （admin 为底本），端差异全部经 props 注入——
 * - filesApi：端 files 三件套（upload/fetchContent/delete，FE-E3b 已薄壳化的 @shared filesCommon 签名）
 * - normalizeFileId：admin onboarding 域 fileId 归一化；缺省 `id => id || ''`（其余三端原语义）
 * - formatError：customer 错误文案映射（t(mapApiError(code,error))）；缺省 `(c, e) => e`
 * - openLabel/closeLabel：customer i18n 文案；缺省中文现值
 * 见 FE-E3b 报告差异表；本模块不 import 门户代码（verify-p2-boundary）。
 */
import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { AlertTriangle, FileText, Upload, X } from 'lucide-react';
import { CredentialLightbox } from './CredentialLightbox';
import type { FileContentResult, FileUploadResult, FileUploadSlot } from '../api/filesCommon';

export type { FileUploadSlot };

/** 端文件 API 三件套（各端 src/api/files 薄壳导出，签名与 @shared/api/filesCommon 一致） */
export interface CredentialFilesApi {
  upload: (file: File, slot: FileUploadSlot, directory?: string) => Promise<
    { ok: true; data: FileUploadResult } | { ok: false; error: string; code?: string; traceId?: string }
  >;
  fetchContent: (key: string) => Promise<FileContentResult>;
  delete: (key: string) => Promise<unknown>;
}

export const IDENTITY_ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
export const FUND_ACCEPT =
  '.jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf';

export type CredentialMode = 'submitter' | 'reviewer';

export type CredentialValue = {
  fileId: string;
  fileName?: string | null;
  contentType?: string | null;
  scanStatus?: string | null;
};

export type CredentialPreviewState = {
  ready: boolean;
  kind: 'empty' | 'loading' | 'image' | 'pdf' | 'unsupported' | 'error';
  warning?: string;
  errorCode?: string;
};

const IDENTITY_SAVE_AS = '请另存为 jpg/png/webp';
const LAZY_LOAD_HINT = '点击加载预览';

function isHeic(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
}

function isPdfFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  return type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

function isImageFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === 'image/jpeg' ||
    type === 'image/jpg' ||
    type === 'image/png' ||
    type === 'image/webp' ||
    /\.(jpe?g|png|webp)$/.test(name)
  );
}

export function validateCredentialFile(file: File, slot: FileUploadSlot): string | null {
  if (file.size === 0) {
    return '文件不能为空';
  }
  if (file.size > 10 * 1024 * 1024) {
    return '单文件不能超过 10MB';
  }
  if (slot === 'IDENTITY') {
    if (isHeic(file) || isPdfFile(file) || !isImageFile(file)) {
      return IDENTITY_SAVE_AS;
    }
    return null;
  }
  if (isHeic(file)) {
    return IDENTITY_SAVE_AS;
  }
  if (!isImageFile(file) && !isPdfFile(file)) {
    return '仅允许 jpg/png/webp 或 PDF';
  }
  return null;
}

export function isScanBlocking(scanStatus?: string | null): boolean {
  const u = (scanStatus || 'CLEAN').toUpperCase();
  return u === 'PENDING' || u === 'REJECTED';
}

function numberedSlotLabel(base: string | undefined, index: number, total: number): string | undefined {
  const name = (base || '').trim();
  if (!name) {
    return undefined;
  }
  if (total <= 1) {
    return name;
  }
  return `${name} ${index + 1}`;
}

function isPdfMeta(contentType?: string | null, fileName?: string | null, blobType?: string): boolean {
  const t = `${contentType || ''} ${blobType || ''}`.toLowerCase();
  const n = (fileName || '').toLowerCase();
  return t.includes('pdf') || n.endsWith('.pdf');
}

function isImageMeta(contentType?: string | null, fileName?: string | null, blobType?: string): boolean {
  const t = `${contentType || ''} ${blobType || ''}`.toLowerCase();
  const n = (fileName || '').toLowerCase();
  return (
    t.includes('jpeg') ||
    t.includes('jpg') ||
    t.includes('png') ||
    t.includes('webp') ||
    /\.(jpe?g|png|webp)$/.test(n)
  );
}

function isReviewerSoftFail(status?: number, code?: string, error?: string): boolean {
  const c = (code || '').toUpperCase();
  const e = (error || '').toUpperCase();
  if (status === 403 || status === 404) {
    return true;
  }
  if (c === 'AUTH_403' || c === 'VAL_400') {
    return true;
  }
  return e.includes('AUTH_403') || e.includes('VAL_400') || e.includes('HTTP 403') || e.includes('HTTP 404');
}

export function CredentialPreview({
  fileId,
  fileName,
  contentType,
  scanStatus,
  slot,
  mode = 'submitter',
  lazy,
  label,
  onStateChange,
  className,
  filesApi,
  normalizeFileId = (id) => id || '',
  formatError = (_code, error) => error,
  openLabel = '打开',
  closeLabel = '关闭',
  showEmptySlotLabel = false,
}: {
  fileId?: string | null;
  fileName?: string | null;
  contentType?: string | null;
  scanStatus?: string | null;
  slot: FileUploadSlot;
  mode?: CredentialMode;
  lazy?: boolean;
  label?: string;
  onStateChange?: (state: CredentialPreviewState) => void;
  className?: string;
  /** 端文件 API 三件套（必传注入） */
  filesApi: CredentialFilesApi;
  /** admin onboarding 域 fileId 归一化；缺省原语义（空值收敛为 ''） */
  normalizeFileId?: (id?: string | null) => string;
  /** 错误文案映射（customer：t(mapApiError(code,error))）；缺省直透 error */
  formatError?: (code: string | undefined, error: string) => string;
  /** 打开按钮文案；缺省 '打开'（三端现值） */
  openLabel?: string;
  /** 关闭 aria-label 与灯箱关闭文案；缺省 '关闭'（三端现值） */
  closeLabel?: string;
  /** 「未上传」空态是否显示槽位名行；缺省 false（agent/merchant/customer 原空态无此行），
   *  admin 原空态有（审查 I1：注入开关保三端空态 JSX 与原版逐字等价） */
  showEmptySlotLabel?: boolean;
}) {
  const boundId = normalizeFileId(fileId);
  const lazyEnabled = lazy ?? mode === 'reviewer';
  const [lazyArmed, setLazyArmed] = useState(false);
  const [lazyArmKey, setLazyArmKey] = useState(boundId);
  const [kind, setKind] = useState<CredentialPreviewState['kind']>(
    boundId && !lazyEnabled ? 'loading' : 'empty',
  );
  const [src, setSrc] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | undefined>(
    boundId && lazyEnabled ? LAZY_LOAD_HINT : undefined,
  );
  const [lightbox, setLightbox] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const onStateRef = useRef(onStateChange);
  onStateRef.current = onStateChange;
  if (lazyArmKey !== boundId) {
    setLazyArmKey(boundId);
    setLazyArmed(false);
  }

  const emit = useCallback((state: CredentialPreviewState) => {
    onStateRef.current?.(state);
  }, []);

  useEffect(() => {
    if (!boundId) {
      setKind('empty');
      setSrc(null);
      setWarning(undefined);
      emit({ ready: false, kind: 'empty' });
      return;
    }
    if (lazyEnabled && !lazyArmed) {
      setKind('empty');
      setSrc(null);
      setWarning(LAZY_LOAD_HINT);
      emit({ ready: mode === 'reviewer', kind: 'empty', warning: LAZY_LOAD_HINT });
      return;
    }
    let cancelled = false;
    setKind('loading');
    setWarning(undefined);
    emit({ ready: false, kind: 'loading' });
    void filesApi.fetchContent(boundId).then((res) => {
      if (cancelled) {
        return;
      }
      if (!res.ok) {
        const soft = isReviewerSoftFail(res.status, res.code, res.error);
        const msg =
          mode === 'reviewer'
            ? soft
              ? `预览失败（${res.code || res.status || res.error}），仍可终审`
              : `预览失败：${formatError(res.code, res.error)}（仍可终审）`
            : formatError(res.code, res.error) || '预览失败';
        setKind('error');
        setWarning(msg);
        emit({
          ready: mode === 'reviewer',
          kind: 'error',
          warning: msg,
          errorCode: res.code,
        });
        return;
      }
      const blobType = res.blob.type || '';
      const name = res.fileName || fileName || null;
      const pdf = isPdfMeta(contentType, name, blobType);
      const image = isImageMeta(contentType, name, blobType) || blobType.startsWith('image/');
      if (pdf && !image) {
        if (slot === 'IDENTITY') {
          setKind('unsupported');
          setWarning('不支持预览');
          emit({ ready: mode === 'reviewer', kind: 'unsupported', warning: '不支持预览' });
          return;
        }
        const url = URL.createObjectURL(res.blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrlRef.current = url;
        setSrc(url);
        setKind('pdf');
        const blocked = isScanBlocking(scanStatus);
        emit({
          ready: mode === 'reviewer' ? true : !blocked,
          kind: 'pdf',
          warning: blocked ? '扫描未完成，暂不可提交' : undefined,
        });
        return;
      }
      const url = URL.createObjectURL(res.blob);
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      objectUrlRef.current = url;
      setSrc(url);
      setKind('image');
      const blocked = isScanBlocking(scanStatus);
      emit({
        ready: mode === 'reviewer' ? true : !blocked,
        kind: 'image',
        warning: blocked ? '扫描未完成，暂不可提交' : undefined,
      });
    });
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setSrc(null);
      setLightbox(false);
      setPdfOpen(false);
    };
  }, [boundId, fileName, contentType, scanStatus, slot, mode, emit, lazyEnabled, lazyArmed]);

  const slotLabel = (label || '').trim();

  if (!boundId) {
    return (
      <div className={`rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-400 ${className || ''}`}>
        {showEmptySlotLabel && slotLabel ? <p className="text-[11px] text-slate-400 mb-2">{slotLabel}</p> : null}
        未上传
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${className || ''}`}>
      <input type="hidden" value={boundId} readOnly />
      {slotLabel ? <p className="text-[11px] text-slate-400 mb-2">{slotLabel}</p> : null}
      {lazyEnabled && !lazyArmed ? (
        <button
          type="button"
          className="w-full py-6 text-xs text-brand-600 hover:underline cursor-pointer"
          onClick={() => setLazyArmed(true)}
        >
          {LAZY_LOAD_HINT}
        </button>
      ) : (
        <>
          {kind === 'loading' && <p className="text-xs text-slate-400">预览加载中…</p>}
          {kind === 'image' && src && (
            <button
              type="button"
              className="block w-full cursor-pointer"
              onClick={() => setLightbox(true)}
            >
              <img src={src} alt={slotLabel} className="max-h-40 mx-auto rounded object-contain" />
            </button>
          )}
          {kind === 'pdf' && (
            <div className="flex items-center gap-2">
              <FileText className="w-8 h-8 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                {slotLabel ? <p className="text-xs text-slate-700 truncate">{slotLabel}</p> : null}
                <button
                  type="button"
                  className="text-xs text-brand-600 hover:underline cursor-pointer mt-0.5"
                  onClick={() => setPdfOpen(true)}
                >
                  {openLabel}
                </button>
              </div>
            </div>
          )}
          {kind === 'unsupported' && (
            <div className="flex items-center gap-2">
              <FileText className="w-8 h-8 text-slate-300 shrink-0" />
              <div className="min-w-0">
                {slotLabel ? <p className="text-xs text-slate-700 truncate">{slotLabel}</p> : null}
                <p className="text-[11px] text-slate-400 mt-0.5">不支持预览</p>
              </div>
            </div>
          )}
          {kind === 'error' && (
            <div className="flex items-start gap-2 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>{warning || '预览失败'}</p>
            </div>
          )}
        </>
      )}
      {isScanBlocking(scanStatus) && kind !== 'error' && (
        <p className="text-[11px] text-amber-700 mt-1">扫描未通过，暂不可提交</p>
      )}
      <CredentialLightbox open={lightbox} src={src} alt={slotLabel} closeLabel={closeLabel} onClose={() => setLightbox(false)} />
      {pdfOpen && src && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/70" onClick={() => setPdfOpen(false)} />
          <button
            type="button"
            className="absolute top-4 right-4 z-10 rounded-full bg-white/90 p-2 text-slate-700 hover:bg-white cursor-pointer"
            onClick={() => setPdfOpen(false)}
            aria-label={closeLabel}
          >
            <X className="w-5 h-5" />
          </button>
          <iframe title={slotLabel} src={src} className="relative z-[1] w-[90vw] h-[90vh] bg-white rounded-lg" />
        </div>
      )}
    </div>
  );
}

export function CredentialUploadField({
  slot,
  value,
  mode = 'submitter',
  disabled,
  directory,
  label,
  onChange,
  onReadyChange,
  filesApi,
  normalizeFileId = (id) => id || '',
  formatError = (_code, error) => error,
  openLabel,
  closeLabel,
  showEmptySlotLabel,
}: {
  slot: FileUploadSlot;
  value?: CredentialValue | null;
  mode?: CredentialMode;
  disabled?: boolean;
  directory?: string;
  label?: string;
  onChange: (next: CredentialValue | null) => void;
  onReadyChange?: (ready: boolean) => void;
  /** 注入透传（见组件头注释；端壳绑定本端 files 三件套与文案口径） */
  filesApi: CredentialFilesApi;
  normalizeFileId?: (id?: string | null) => string;
  formatError?: (code: string | undefined, error: string) => string;
  openLabel?: string;
  closeLabel?: string;
  /** 空态槽位名行开关（透传 CredentialPreview；缺省 false=三端原状） */
  showEmptySlotLabel?: boolean;

}) {
  const [pending, setPending] = useState<CredentialValue | null>(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const display = pending ?? value ?? null;

  const handleState = (state: CredentialPreviewState) => {
    if (pending) {
      if (state.kind === 'loading' || state.kind === 'empty') {
        return;
      }
      if (state.kind === 'error' || state.kind === 'unsupported') {
        setLocalError(state.warning || '预览失败，未替换已上传影像');
        setPending(null);
        onReadyChange?.(false);
        return;
      }
      if (state.kind === 'image' || state.kind === 'pdf') {
        const oldId = value?.fileId;
        if (oldId && oldId !== pending.fileId) {
          void filesApi.delete(oldId).catch(() => undefined);
        }
        onChange(pending);
        setPending(null);
        setLocalError('');
        onReadyChange?.(state.ready);
        return;
      }
      setLocalError(state.warning || '预览失败，未替换已上传影像');
      setPending(null);
      onReadyChange?.(false);
      return;
    }
    onReadyChange?.(state.ready);
  };

  const onPick = async (file?: File) => {
    if (!file || disabled) {
      return;
    }
    const err = validateCredentialFile(file, slot);
    if (err) {
      setLocalError(err);
      return;
    }
    setBusy(true);
    setLocalError('');
    const res = await filesApi.upload(file, slot, directory);
    setBusy(false);
    if (!res.ok || !res.data.fileId) {
      setLocalError(res.ok ? '上传失败，未返回文件' : formatError(res.code, res.error));
      return;
    }
    setPending({
      fileId: res.data.fileId,
      contentType: res.data.contentType,
      scanStatus: res.data.scanStatus || 'CLEAN',
    });
  };

  const openPicker = () => {
    if (disabled || busy) {
      return;
    }
    inputRef.current?.click();
  };

  // F-007：惰性区点击转发——命中交互元素或浮层（灯箱/pdf 预览）不转发，避免双触发
  const handleRootClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest?.('label, button, input, a')) {
      return;
    }
    if (target.closest?.('[role="dialog"]')) {
      return;
    }
    openPicker();
  };

  return (
    <div
      className={`space-y-2 ${disabled || busy ? '' : 'cursor-pointer'}`}
      onClick={handleRootClick}
    >
      {label && !display ? <p className="text-xs text-slate-500">{label}</p> : null}
      {display && (
        <CredentialPreview
          filesApi={filesApi}
          normalizeFileId={normalizeFileId}
          formatError={formatError}
          openLabel={openLabel}
          closeLabel={closeLabel}
          showEmptySlotLabel={showEmptySlotLabel}
          fileId={display.fileId}
          fileName={display.fileName}
          contentType={display.contentType}
          scanStatus={display.scanStatus}
          slot={slot}
          mode={mode}
          lazy={false}
          label={label}
          onStateChange={handleState}
        />
      )}
      <label className={`relative flex items-center gap-2 input cursor-pointer overflow-hidden ${disabled || busy ? 'opacity-60' : ''}`}>
        <Upload className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-600 truncate">
          {busy ? '上传中…' : display ? '更换影像' : '选择文件'}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={slot === 'IDENTITY' ? IDENTITY_ACCEPT : FUND_ACCEPT}
          className="absolute inset-0 z-[1] h-full w-full cursor-pointer opacity-0"
          aria-label={display ? '更换影像' : '选择文件'}
          disabled={disabled || busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            void onPick(file);
          }}
        />
      </label>
      <p className="text-[11px] text-slate-400">
        {slot === 'IDENTITY' ? '仅 jpg / png / webp，单文件 ≤10MB' : 'jpg / png / webp 或 PDF，单文件 ≤10MB'}
      </p>
      {localError && <p className="text-xs text-red-500">{localError}</p>}
    </div>
  );
}

export function CredentialUploadList({
  slot,
  values,
  max,
  mode = 'submitter',
  disabled,
  directory,
  label,
  addLabel = '添加影像',
  onChange,
  onReadyChange,
  filesApi,
  normalizeFileId,
  formatError,
  openLabel,
  closeLabel,
  showEmptySlotLabel,
}: {
  slot: FileUploadSlot;
  values: CredentialValue[];
  max: number;
  mode?: CredentialMode;
  disabled?: boolean;
  directory?: string;
  label?: string;
  addLabel?: string;
  onChange: (next: CredentialValue[]) => void;
  onReadyChange?: (ready: boolean) => void;
  /** 注入透传（内部逐项传给 CredentialUploadField） */
  filesApi: CredentialFilesApi;
  normalizeFileId?: (id?: string | null) => string;
  formatError?: (code: string | undefined, error: string) => string;
  openLabel?: string;
  closeLabel?: string;
  /** 空态槽位名行开关（透传 CredentialPreview；缺省 false=三端原状） */
  showEmptySlotLabel?: boolean;

}) {
  const [readyMap, setReadyMap] = useState<Record<string, boolean>>({});
  const ids = values.map((v) => v.fileId).join('|');
  const allReady = values.length > 0 && values.every((v) => readyMap[v.fileId]);
  useEffect(() => {
    onReadyChange?.(allReady);
  }, [allReady, onReadyChange]);
  useEffect(() => {
    setReadyMap((prev) => {
      const next: Record<string, boolean> = {};
      values.forEach((v) => {
        next[v.fileId] = prev[v.fileId] ?? false;
      });
      return next;
    });
    // ids 变化时按新列表收敛 readyMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const atMax = values.length >= max;

  return (
    <div className="space-y-3">
      {values.map((v, i) => (
        <div key={v.fileId} className="relative">
          <CredentialUploadField
            slot={slot}
            value={v}
            mode={mode}
            disabled={disabled}
            filesApi={filesApi}
            normalizeFileId={normalizeFileId}
            formatError={formatError}
            openLabel={openLabel}
            closeLabel={closeLabel}
            showEmptySlotLabel={showEmptySlotLabel}
            directory={directory}
            label={slot === 'IDENTITY' ? label : numberedSlotLabel(label, i, values.length)}
            onChange={(next) => {
              if (!next) {
                onChange(values.filter((_, j) => j !== i));
                return;
              }
              onChange(values.map((x, j) => (j === i ? next : x)));
            }}
            onReadyChange={(ok) => setReadyMap((m) => ({ ...m, [v.fileId]: ok }))}
          />
        </div>
      ))}
      {!atMax && (
        <CredentialUploadField
          slot={slot}
          value={null}
          mode={mode}
          disabled={disabled}
            filesApi={filesApi}
          normalizeFileId={normalizeFileId}
          formatError={formatError}
          openLabel={openLabel}
          closeLabel={closeLabel}
          showEmptySlotLabel={showEmptySlotLabel}
                    directory={directory}
          label={values.length === 0 ? label : addLabel}
          onChange={(next) => {
            if (!next) {
              return;
            }
            if (values.length >= max) {
              return;
            }
            onChange([...values, next]);
          }}
          onReadyChange={() => undefined}
        />
      )}
      {atMax && (
        <p className="text-[11px] text-slate-400">已达上限 {max} 张，不可再添加</p>
      )}
    </div>
  );
}

export default CredentialPreview;
